import { Component, ErrorInfo, PropsWithChildren, ReactNode } from 'react';
import { trackException } from 'utills/telemetry';

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * ErrorBoundary catches unhandled render/lifecycle errors thrown by its child
 * component tree, reports them via telemetry, and renders a fallback message
 * instead of letting the error crash the app.
 */
export class ErrorBoundary extends Component<PropsWithChildren<{}>, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  /**
   * Derives the next state after a descendant throws, flagging the error
   * so render() can swap in the fallback UI.
   * @returns state indicating an error was caught
   */
  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  /**
   * Reports the caught error and its component stack to telemetry.
   * @param error the error thrown by a descendant component
   * @param errorInfo React-provided info, including the component stack
   */
  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    trackException(error, { componentStack: errorInfo.componentStack || '' });
  }

  /**
   * Renders the fallback message once an error has been caught, otherwise
   * renders children as normal.
   */
  render(): ReactNode {
    if (this.state.hasError) {
      return <div>Something went wrong. Please refresh and try again.</div>;
    }
    return this.props.children;
  }
}
